import type { CopyVehicleScheduleFilter, DriveType } from '@/api/main/types.gen'
import {
  calendarDateInTimezone,
  completeMappings,
  isIdentityCopyOnSameDay,
  shiftCalendarDate,
} from './autoScheduleCopy'
import { intervalsMatch, type TimeInterval } from './autoScheduleIntervals'

export const DEFAULT_DISTRICTS_DRIVE_TYPE: DriveType[] = ['statistical', 'evidence']

const sameSet = <T>(a: readonly T[], b: readonly T[]): boolean => {
  if (a.length !== b.length) {
    return false
  }
  const other = new Set(b)
  return a.every(item => other.has(item))
}

export type RequirementsSnapshot = {
  districtIds: number[]
  ignoreCooldown: boolean
  combinationKeys: string[]
}

export const defaultRequirementsSnapshot = (combinationKeys: string[]): RequirementsSnapshot => ({
  districtIds: [],
  ignoreCooldown: false,
  combinationKeys,
})

export const isRequirementsDirty = (current: RequirementsSnapshot, initial: RequirementsSnapshot): boolean =>
  !sameSet(current.districtIds, initial.districtIds) ||
  current.ignoreCooldown !== initial.ignoreCooldown ||
  !sameSet(current.combinationKeys, initial.combinationKeys)

export const hasRequirementsPayload = (districtIds: number[]): boolean => districtIds.length > 0

export type DistrictsSnapshot = {
  districtIds: number[]
  driveType: DriveType[]
}

export const defaultDistrictsSnapshot = (): DistrictsSnapshot => ({
  districtIds: [],
  driveType: [...DEFAULT_DISTRICTS_DRIVE_TYPE],
})

export const isDistrictsDirty = (current: DistrictsSnapshot, initial: DistrictsSnapshot): boolean =>
  !sameSet(current.districtIds, initial.districtIds) || !sameSet(current.driveType, initial.driveType)

export const hasDistrictsPayload = (districtIds: number[], driveType: DriveType[]): boolean =>
  districtIds.length > 0 && driveType.length > 0

export type CopyMappingSnapshot = {
  sourceVehicleId: string
  destinationVehicleId: string
}

export type CopySnapshot = {
  sourceDate: string
  destinationDate: string
  filter: CopyVehicleScheduleFilter
  mappings: CopyMappingSnapshot[]
}

export const defaultCopySnapshot = (vehicleId: string, targetWindowStart: string, timeZone: string): CopySnapshot => {
  const destinationDate = calendarDateInTimezone(targetWindowStart, timeZone)
  return {
    sourceDate: shiftCalendarDate(destinationDate, -7, timeZone),
    destinationDate,
    filter: 'all',
    mappings: [{ sourceVehicleId: vehicleId, destinationVehicleId: vehicleId }],
  }
}

const sameMappings = (a: CopyMappingSnapshot[], b: CopyMappingSnapshot[]): boolean =>
  a.length === b.length &&
  a.every(
    (row, index) =>
      row.sourceVehicleId === b[index]?.sourceVehicleId && row.destinationVehicleId === b[index]?.destinationVehicleId
  )

export const isCopyDirty = (current: CopySnapshot, initial: CopySnapshot): boolean =>
  current.sourceDate !== initial.sourceDate ||
  current.destinationDate !== initial.destinationDate ||
  current.filter !== initial.filter ||
  !sameMappings(current.mappings, initial.mappings)

export const canSubmitCopy = (
  mappings: CopyMappingSnapshot[],
  sourceDate: string,
  destinationDate: string
): boolean => {
  const complete = completeMappings(mappings)
  return complete.length > 0 && !complete.some(mapping => isIdentityCopyOnSameDay(mapping, sourceDate, destinationDate))
}

export const isIntervalDirty = (current: TimeInterval, initial: TimeInterval): boolean =>
  !intervalsMatch(current, initial)

export const isModalDirty = (intervalDirty: boolean, tabDirty: boolean): boolean => intervalDirty || tabDirty

export const shouldConfirmCancel = (dirty: boolean): boolean => dirty

export const shouldConfirmSubmit = (hasPayload: boolean): boolean => hasPayload
